// 1. Imports
import "./SwipeableModals.scss";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  IconButton,
  InputAdornment,
} from "@mui/material";

import AddPhoto from "./../../../assets/add-photo.png";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  selectedItem,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    password: "",
    email: "",
    role_id: "",
  });
  const [image, setImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Handle password change form
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Fetch roles for dropdown
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get("/roles");
        if (response.data && response.data.data) {
          setRoles(response.data.data);
        }
      } catch (error) {
        toast.error("فشل في تحميل الصلاحيات");
      }
    };

    if (
      open &&
      (currentView === "createAdmin" || currentView === "editAdmin")
    ) {
      fetchRoles();
    }
  }, [open, currentView]);

  // Set form data if editing
  useEffect(() => {
    if (selectedItem && currentView === "editAdmin") {
      setFormData({
        name: selectedItem.name || "",
        phone: selectedItem.phone || "",
        email: selectedItem.email || "",
        role_id: selectedItem.role_id || "",
        // Don't include password in edit mode
      });

      // If user has image, set it
      if (selectedItem.image) {
        // Just set a flag for existing image
        setImage("existing");
      } else {
        setImage(null);
      }
      setRemoveImage(false);
    } else if (currentView === "createAdmin") {
      // Reset form when creating new admin
      setFormData({
        name: "",
        phone: "",
        password: "",
        email: "",
        role_id: "",
      });
      setImage(null);
      setRemoveImage(false);
    }
  }, [selectedItem, currentView, open]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle image change
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
      setRemoveImage(false);
    }
  };

  // Handle removing image
  const handleRemoveImage = () => {
    setImage(null);
    setRemoveImage(true);
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Check if we need to handle image upload
      if ((image && image !== "existing") || removeImage) {
        // Use FormData for image uploads or removals
        const data = new FormData();

        // Add form fields
        Object.keys(formData).forEach((key) => {
          if (formData[key] !== "" || key === "role_id") {
            data.append(key, formData[key]);
          }
        });

        // Add image if selected
        if (image && image !== "existing") {
          data.append("image", image);
        }

        // Handle image removal
        if (removeImage) {
          data.append("remove_image", "1");
        }

        if (currentView === "createAdmin") {
          await api.post("/supervisors", data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          toast.success("تم إضافة المسؤول بنجاح");
        } else if (currentView === "editAdmin") {
          await api.put(`/supervisors/${selectedItem.id}`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          toast.success("تم تعديل بيانات المسؤول بنجاح");
        }
      } else {
        // Use x-www-form-urlencoded for regular form submissions without images
        const urlEncodedData = new URLSearchParams();

        // Add form fields
        Object.keys(formData).forEach((key) => {
          if (formData[key] !== "" || key === "role_id") {
            urlEncodedData.append(key, formData[key]);
          }
        });

        if (currentView === "createAdmin") {
          await api.post("/supervisors", urlEncodedData, {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          });
          toast.success("تم إضافة المسؤول بنجاح");
        } else if (currentView === "editAdmin") {
          await api.put(`/supervisors/${selectedItem.id}`, urlEncodedData, {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          });
          toast.success("تم تعديل بيانات المسؤول بنجاح");
        }
      }

      // Close modal and refresh data
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error.response?.data?.message || "حدث خطأ أثناء حفظ البيانات"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { id, value } = e.target;
    setPasswordData({
      ...passwordData,
      [id === "new-password" ? "newPassword" : "confirmPassword"]: value,
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!selectedItem?.id) {
      toast.error("لم يتم تحديد المستخدم");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("كلمة المرور وتأكيدها غير متطابقين");
      return;
    }

    setPasswordLoading(true);

    try {
      await api.post(`/supervisors/${selectedItem.id}/change-password`, {
        password: passwordData.newPassword,
      });

      toast.success("تم تغيير كلمة المرور بنجاح");
      onClose();
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(
        error.response?.data?.message || "حدث خطأ أثناء تغيير كلمة المرور"
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const renderAdminForm = () => (
    <div className="user-modal">
      <p className="user-modal-p">
        {currentView === "createAdmin"
          ? "اضافة مسؤول فرعي"
          : "تعديل مسؤول فرعي"}
      </p>
      <form onSubmit={handleSubmit}>
        <div className="form-img">
          <label htmlFor="image" className="img-control">
            <img
              src={
                image instanceof File
                  ? URL.createObjectURL(image)
                  : image === "existing" && selectedItem?.image && !removeImage
                  ? selectedItem.image
                  : AddPhoto
              }
              alt=""
            />
            <span>اضافة صورة شخصية</span>
          </label>
          <input
            type="file"
            id="image"
            onChange={handleImageChange}
            accept="image/*"
          />
          {(image || (image === "existing" && !removeImage)) && (
            <div className="remove-image" onClick={handleRemoveImage}>
              <HighlightOffIcon />
              <span>إزالة الصورة</span>
            </div>
          )}
        </div>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="name"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الاسم
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            notched
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="phone"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            رقم الموبايل
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="phone"
            name="phone"
            type="text"
            value={formData.phone}
            onChange={handleChange}
            notched
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="email"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            البريد الالكتروني
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            notched
          />
        </FormControl>

        {/* Only show password field in create mode */}
        {currentView === "createAdmin" && (
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              كلمة المرور
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              notched
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        )}

        <FormControl fullWidth>
          <InputLabel
            htmlFor="role_id"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الصلاحية
          </InputLabel>
          <Select
            labelId="role-select-label"
            id="role_id"
            name="role_id"
            value={formData.role_id}
            onChange={handleChange}
            displayEmpty={true}
            sx={{ borderRadius: "8px" }}
            required
          >
            <MenuItem value="">
              <em>اختر الصلاحية</em>
            </MenuItem>
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <BlueButton
          text={currentView === "createAdmin" ? "اضافة" : "تعديل"}
          size="medium"
          variant="fill"
          type="submit"
          loading={loading}
          disable={loading}
        />
      </form>
    </div>
  );

  const renderEditPasswordView = () => (
    <div className="user-modal">
      <p className="user-modal-p">تغيير كلمة المرور</p>
      <form onSubmit={handlePasswordSubmit}>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="new-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            كلمة المرور الجديدة
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="new-password"
            type={showNewPassword ? "text" : "password"}
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
            notched
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="confirm-password"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            تأكيد كلمة المرور
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
            notched
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        <BlueButton
          text="تغيير"
          size="medium"
          variant="fill"
          type="submit"
          loading={passwordLoading}
          disable={passwordLoading}
        />
      </form>
    </div>
  );

  const renderDefaultView = () => <div></div>;

  const views = {
    createAdmin: renderAdminForm,
    editAdmin: renderAdminForm,
    changePassword: renderEditPasswordView,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
